<template>
  <UIPanel v-model="clearedSearch" v-model:storage="searchStorage" :disabled :placeholder>
    <template
      #default="{
        ButtonCancel,
        ButtonDelete,
        ButtonDuplicate,
        ButtonExportAdvanced,
        ButtonImport,
        ButtonNew,
        ButtonSaveChanges,
        size,
        shrink,
      }"
    >
      <template v-if="!(shrink && hasSelectedItems)">
        <UIFilter
          v-model="clearedFilter"
          v-model:storage="computedFilterStorage"
          :disabled
          :size
          :fields="filterFields"
          flat
          :unlabeled="shrink"
          class="ui-grid-bunch__button-filter"
        />
        <UISort
          v-model="clearedSort"
          v-model:storage="computedSortStorage"
          :disabled
          :size
          :fields="sortFields"
          :unlabeled="shrink"
          class="ui-grid-bunch__button-sort"
        />
      </template>

      <slot name="panel" :size />

      <template v-if="hasSelectedItems">
        <component
          :is="ButtonDuplicate"
          v-if="isDuplicateButtonVisible"
          :disabled
          @click="handleClickDuplicate"
        />
        <component
          :is="ButtonDelete"
          v-if="isDeleteButtonVisible"
          :disabled
          @click="handleClickDelete"
        />
      </template>

      <template v-if="isExportButtonVisible">
        <component
          :is="ButtonExportAdvanced"
          :has-filters="hasFilters"
          :has-selected-items="hasSelectedItems"
          :disabled
          class="ui-grid-bunch__button-export"
          @export:all="handleClickExport"
          @export:filtered="handleClickExportFiltered"
          @export:selected="handleExportSelected"
        />
      </template>
      <component
        :is="ButtonImport"
        v-if="!(shrink && (hasSelectedItems || dirty))"
        :disabled
        class="ui-grid-bunch__button-import"
        @click="handleClickImport"
      />

      <template v-if="dirty">
        <component :is="ButtonSaveChanges" :disabled :unlabeled="shrink" @click="handleClickSave" />
        <component :is="ButtonCancel" :disabled @click="handleClickCancel" />
      </template>

      <slot name="new">
        <component
          :is="ButtonNew"
          :disabled
          class="ui-grid-bunch__button-new"
          @click="handleClickCreate"
        />
      </slot>
    </template>
  </UIPanel>
  <slot name="before" />
  <slot name="list" :items>
    <GridSkeleton
      v-if="loading"
      v-bind="{ footer, sm }"
      :columns="columnsList"
      :message="loadingMessage"
      selectable
      wrapped
    />
    <Grid
      v-else
      ref="gridRef"
      v-model:selected="selected"
      v-model:sort="computedSortStorage"
      v-bind="{
        ...attrs,
        footer,
        items,
        lazy,
        sm,
        valueGetter,
        valuePaster,
        valueCopier,
      }"
      :data-refid="dataRefid"
      :columns="columnsList"
      :insertable-rows
      editable
      selectable
      scrollable
      wrapped
      @click:menu="handleClickMenu"
      @click:row="handleClickRow"
      @click:insert-row="handleClickInsertRow"
    >
      <template #prefix="{ active, item }">
        <Lock v-if="item.isDisabled" v-bind="{ active }" />
      </template>
      <template #checkbox="{ active, item }"> </template>
      <template #cellEdit="{ active, item }">
        <EditPencil
          v-bind="{ active }"
          :readonly="item.isReadonly"
          @click="handleClickEdit(item.id)"
        />
      </template>
      <template
        v-for="[name, slot] in slots"
        :key="slot"
        #[slot]="{ displayValue, editCell, item }"
      >
        <EditComponent
          v-if="editCell"
          :name
          :instance="item"
          @click:enter="handleEditClickEnter"
          @click:tab="handleEditClickTab"
          @click:escape="handleEditReset"
        />
        <CellValue v-else v-bind="{ name }" :instance="item" :value="displayValue">
          <slot :name="slot" v-bind="{ displayValue, editCell, item }">
            {{ displayValue }}
          </slot>
        </CellValue>
      </template>
      <template #cellState="{ editCell, item }">
        <State v-bind="{ columns, item }" :edit="editCell" @release="handleEditReset" />
      </template>
    </Grid>
  </slot>
</template>

<script setup lang="ts">
import { computed, provide, useTemplateRef } from 'vue'

import { Filter, FilterGroup, Sort } from '@types'
import { GridColumn, GridFooter, GridValuePaster } from './utils/types'
import { EntityState } from '@/entities/utils/enums'
import { Entity } from '@/entities/utils/common'

import { getSlotName } from './utils/helpers'
import { usePowerMode } from '@/hooks/powerMode/powerMode'
import { useNotifications } from '@/plugins/notification'

import CellValue from './components/BunchCellValue.vue'
import EditComponent from './components/BunchEditComponent.vue'
import EditPencil from './components/BunchEditPencil.vue'
import Grid from './Grid.vue'
import GridSkeleton from './GridSkeleton.vue'
import Lock from './components/BunchLock.vue'
import State from './components/BunchState.vue'
import { UIPanel } from '@ui/core'
import { UIFilter, UISort } from '@ui/controllers'

type GridBunchItem = Entity<any>

type Props = {
  columns: GridColumn[]
  items: GridBunchItem[]

  footer?: GridFooter

  disabled?: boolean
  readonly?: boolean

  activeId?: string | number

  sm?: string

  unitarySort?: boolean

  displayMenu?: boolean

  collapseWidth?: number

  lazy?: number

  // own props
  dataRefid?: string
  dirty: boolean

  disableSortState?: boolean
  disallowDuplicate?: boolean

  placeholder?: string

  loading?: boolean
  loadingMessage?: string
  processing?: boolean
  insertableRows?: boolean

  handleBlockers?: () => Promise<unknown>
}

type Emits = {
  'click:menu': [data: GridBunchItem]
  'click:row': [data: GridBunchItem]

  'click:edit': [data: string]
  'click:duplicate': [data: string[]]
  'click:delete': [data: string[]]
  'click:save': []
  'click:cancel': []
  'click:export': []
  'click:export-filtered': []
  'click:export-selected': []
  'click:import': []
  'click:create': []
  'click:create-inline': [index: number]
}

const {
  footer,

  dataRefid,
  dirty,

  disableSortState,

  placeholder,

  loading,
  loadingMessage,
  processing,

  insertableRows = true,

  columns,
  ...props
} = defineProps<Props>()
const emit = defineEmits<Emits>()

function deselect() {
  gridRef.value?.deselect()
  selected.value = []
}

defineExpose({
  deselect,
  getContainer() {
    return gridRef.value
  },
})

defineOptions({
  name: 'UIGridBunch',
  inheritAttrs: false,
})

const selected = defineModel<string[]>('selected', { required: true })
const clearedSearch = defineModel<string | undefined>('search', {
  required: true,
})
const searchStorage = defineModel<string | undefined>('searchStorage', {
  required: true,
})

const clearedSort = defineModel<Sort[] | undefined>('sort', { required: true })
const sortStorage = defineModel<Sort[] | undefined>('sortStorage', {
  required: true,
})
const clearedFilter = defineModel<FilterGroup | undefined>('filter', {
  required: true,
})
const filterStorage = defineModel<Filter | undefined>('filterStorage', {
  required: true,
})

const { error, warn } = useNotifications()
const { isPowerMode } = usePowerMode()

const gridRef = useTemplateRef('gridRef')

provide('isGridBunch', true)

const computedFilterStorage = computed({
  get() {
    return filterStorage.value
  },
  async set(value) {
    try {
      await props.handleBlockers?.()
    } catch {
      return
    }
    filterStorage.value = value
  },
})

const computedSortStorage = computed({
  get() {
    return sortStorage.value
  },
  async set(value) {
    try {
      await props.handleBlockers?.()
    } catch {
      return
    }
    sortStorage.value = value
  },
})

const attrs = computed(() => props)

const disabled = computed(() => processing || loading)

const filterableColumns = computed(() => [
  ...columns,
  {
    name: 'state',
    caption: 'State',
    unsortable: disableSortState,
    unresizeable: true,
    default: '5.5rem',
    headerClasses: 'ui-grid__header--state',
    cellClasses: 'ui-grid__cell--state',
    footerClasses: 'ui-grid__footer--state',
    filter: {
      isSimple: true,
      list: Object.entries(EntityState).map(([, value]) => ({
        value,
        label: value,
      })),
    },
    hideCaptionIfCollapsed: true,
  } as GridColumn,
])

const columnsList = computed(() => [
  {
    name: 'edit',
    caption: '',
    unsortable: true,
    unresizeable: true,
    silent: true,
    headerClasses: 'ui-grid__header--edit',
    cellClasses: `ui-grid__cell--edit ${loading ? 'ui-grid__cell--edit-loading' : ''}`,
    footerClasses: 'ui-grid__footer--edit',
    default: 'min-content',
  },
  ...filterableColumns.value,
])

const filterFields = computed(() =>
  filterableColumns.value
    .filter(column => !column.unsortable)
    .map(column => ({
      name: column.name,
      caption: column.caption,
      ...column.filter,
    })),
)

const sortFields = computed(() =>
  filterableColumns.value
    .filter(column => !column.unsortable)
    .map(column => ({
      value: column.filter?.name || column.name,
      label: column.filter?.caption || column.caption,
    })),
)

const slots = computed(() => columns.map(column => [column.name, getSlotName(column.name, 'cell')]))

const hasSelectedItems = computed(() => !!selected.value.length)

const isDuplicateButtonVisible = computed(() => !props.disallowDuplicate)

const isDeleteButtonVisible = computed(() => {
  return selected.value.some(id => {
    const item = props.items.find(item => item.id === id)
    return item && !item.isDisabled
  })
})

const hasFilters = computed(() => {
  return !!clearedSearch.value || !!clearedFilter.value || !!clearedSort.value
})

const isExportButtonVisible = computed(() => {
  return !!props.items.length
})

const hasInvalidValues = computed(() => {
  return props.items.some(item => !item.isValid)
})

const valueGetter = (field: string, item: GridBunchItem) => {
  const displayKey = getSlotName(field, 'display') as keyof GridBunchItem
  return (displayKey && item[displayKey]) || item.field(field).value
}

const valuePaster: GridValuePaster = async (field, item, data, check) => {
  if (item.isDisabled || item.isReadonly) return
  const pasteKey = getSlotName(field, 'paste') as keyof GridBunchItem
  if (check && pasteKey && item[pasteKey]) {
    try {
      await item[pasteKey](data, { isPowerMode: isPowerMode?.value })
    } catch (e) {
      error({ message: e as string })
      return
    }
  } else {
    item.field(field).value = data
  }
}

const valueCopier = (field: string, item: GridBunchItem) => {
  const copyKey = getSlotName(field, 'copy') as keyof GridBunchItem
  return (copyKey && item[copyKey]) || undefined
}

const handleClickMenu = (data: any) => {
  emit('click:menu', data)
}

const handleClickRow = (data: any) => {
  emit('click:row', data)
}

const handleClickEdit = (data: string) => {
  emit('click:edit', data)
}

const handleClickDuplicate = () => {
  emit('click:duplicate', selected.value)
  deselect()
}

const handleClickDelete = () => {
  const nonDisabledIds = selected.value.filter(id => {
    const item = props.items.find(item => item.id === id)
    return item && !item.isDisabled
  })
  emit('click:delete', nonDisabledIds)
  deselect()
}

const handleClickSave = () => {
  if (!hasInvalidValues.value) {
    emit('click:save')
    return
  }
  warn({
    message: 'Warning',
    description:
      'You have invalid data in the grid. If you continue, your changes will be saved, but invalid data will be ignored and not saved.',
    actionRequired: true,
    actions: [
      {
        label: 'Save data',
        buttonVariant: 'primary',
        onClick: remove => {
          emit('click:save')
          remove?.()
        },
      },
      {
        label: 'Cancel saving',
        buttonVariant: 'secondary',
        onClick: remove => {
          remove?.()
        },
      },
    ],
  })
}

const handleClickCancel = () => {
  emit('click:cancel')
}

const handleClickExport = () => {
  emit('click:export')
}

const handleClickExportFiltered = () => {
  emit('click:export-filtered')
}

const handleExportSelected = () => {
  emit('click:export-selected')
}

const handleClickImport = () => {
  emit('click:import')
}

const handleClickCreate = () => {
  emit('click:create')
}

const handleClickInsertRow = (index: number) => {
  emit('click:create-inline', index)
}

const handleEditClickEnter = () => {
  gridRef.value?.release()
  gridRef.value?.clickEnter()
}

const handleEditClickTab = () => {
  gridRef.value?.release()
  gridRef.value?.clickTab()
}

const handleEditReset = () => {
  gridRef.value?.release()
}
</script>

<style>
.ui-grid-bunch {
  &__button-filter,
  &__button-sort {
    @apply px-1.5;
  }

  &__button-new {
    @apply ml-auto;
  }
}

.ui-grid__cell {
  &--edit {
    .ui-grid__caption {
      @apply hidden;
    }

    .ui-grid__cell-value {
      @apply py-1 px-2 -mx-2 -my-1;
      @apply text-primary-selected;
      @apply cursor-pointer;

      &--truncated {
        @apply overflow-visible;
      }
    }
  }

  &--edit-loading {
    .ui-grid__cell-value {
      @apply min-w-12;
      @apply p-0 m-0;
      @apply bg-transparent;
    }
  }
}

.ui-grid:not(.ui-grid--collapsed) {
  .ui-grid__header,
  .ui-grid__cell,
  .ui-grid__footer {
    &--edit {
      @apply !px-0;
    }
    &--state {
      @apply !pr-2;
    }
  }

  .ui-grid__cell {
    &--edit {
      .ui-grid__cell-value {
        @apply p-0 m-0;
        @apply bg-transparent dark:bg-transparent;
      }
    }
  }
}

.ui-grid:is(.ui-grid--collapsed) {
  .ui-grid__cell {
    &--state {
      .ui-grid__cell-value--truncated {
        @apply overflow-visible;
      }
      span {
        @apply py-0.5 px-2;
        @apply rounded-full;
        @apply bg-gray-50 dark:bg-gray-800;
      }
    }
  }
}
</style>
